import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { base64ToBlobUrl, downloadBase64 } from '../utils';

interface PreviewModalProps {
  base64: string;
  fileName: string;
  mimeType: string;
  onClose: () => void;
}

export function PreviewModal({ base64, fileName, mimeType, onClose }: PreviewModalProps) {
  const [zoom, setZoom] = useState(1);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const blobUrl = useMemo(() => base64ToBlobUrl(base64, mimeType), [base64, mimeType]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  // Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Image zoom via ctrl+wheel / pinch
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    setZoom(prev => Math.min(5, Math.max(0.25, prev - e.deltaY * 0.005)));
  }, []);

  useEffect(() => {
    const el = imageContainerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const isPdf = mimeType === 'application/pdf';
  const isImage = mimeType.startsWith('image/') && mimeType !== 'image/tiff';

  const modal = (
    <div className="preview-overlay" onClick={onClose}>
      <div className="preview-modal" onClick={e => e.stopPropagation()}>
        <div className="preview-header">
          <span className="preview-filename">{fileName}</span>
          <div className="preview-actions">
            {isImage && zoom !== 1 && (
              <span className="preview-zoom-badge">{Math.round(zoom * 100)}%</span>
            )}
            <button
              className="btn-ghost"
              onClick={() => downloadBase64(base64, fileName, mimeType)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Download</span>
            </button>
            <button className="btn-ghost" onClick={onClose}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="preview-body">
          {isPdf ? (
            <object data={blobUrl} type="application/pdf" className="preview-pdf">
              <div className="preview-fallback">
                <span>PDF preview is not supported in this browser.</span>
                <button className="btn-ghost" onClick={() => downloadBase64(base64, fileName, mimeType)}>
                  <span>Download PDF</span>
                </button>
              </div>
            </object>
          ) : isImage ? (
            <div className="preview-image-container" ref={imageContainerRef}>
              <img
                src={blobUrl}
                alt={fileName}
                className="preview-image"
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
            </div>
          ) : (
            <div className="preview-fallback">
              <span>Preview is not available for this file type.</span>
              <button className="btn-ghost" onClick={() => downloadBase64(base64, fileName, mimeType)}>
                <span>Download File</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
